import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Mail, FileText, Send, ClipboardList } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, Field, TextInput, TextArea } from "./kit";

const TABS = [
  { key: "compose", label: "Compose", icon: Send },
  { key: "contacts", label: "Lead Directory", icon: ClipboardList },
  { key: "templates", label: "Templates", icon: FileText },
  { key: "log", label: "Sent Log", icon: Mail },
];

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "" });

  const load = () => api.get("/admin/contacts").then(({ data }) => setContacts(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.email) return toast.error("Email is required");
    await api.post("/admin/contacts", form);
    setForm({ name: "", email: "", company: "", country: "" });
    toast.success("Lead added to directory");
    load();
  };

  const importCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/contacts/import", fd);
    toast.success(`Imported ${data.imported} leads`);
    load();
  };

  const remove = async (c) => {
    await api.delete(`/admin/contacts/${c.id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <Card title="Add lead" desc="Manually add a contact to the email directory.">
        <div className="grid sm:grid-cols-4 gap-3">
          <TextInput value={form.name} placeholder="Name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextInput value={form.email} placeholder="Email" onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <TextInput value={form.company} placeholder="Company" onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <TextInput value={form.country} placeholder="Country" onChange={(e) => setForm({ ...form, country: e.target.value })} />
        </div>
        <button className="btn btn-sm btn-primary mt-4" onClick={add} data-testid="contact-add-button">Add Lead</button>
      </Card>

      <Card title="CSV import" desc="Upload a CSV with columns: name, email, company, country.">
        <label className="inline-flex items-center gap-2 btn btn-sm btn-outline-light cursor-pointer">
          <Upload size={14} /> Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} data-testid="contact-csv-import" />
        </label>
      </Card>

      <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6">
        <h3 className="font-display font-bold text-white mb-4">Directory ({contacts.length})</h3>
        <table className="w-full text-sm" data-testid="admin-contacts-table">
          <thead>
            <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-white/10">
              <th className="pb-3 font-semibold">Name</th><th className="pb-3 font-semibold">Email</th><th className="pb-3 font-semibold">Company</th><th className="pb-3 font-semibold">Country</th><th className="pb-3" />
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-b border-white/5">
                <td className="py-2.5 text-white font-semibold">{c.name}</td>
                <td className="py-2.5 text-slate-300">{c.email}</td>
                <td className="py-2.5 text-slate-300">{c.company || "—"}</td>
                <td className="py-2.5 text-slate-300">{c.country || "—"}</td>
                <td className="py-2.5 text-right"><button onClick={() => remove(c)} className="p-1.5 text-slate-500 hover:text-red-400" aria-label="Delete contact"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {contacts.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-500">No contacts yet — add one or import a CSV.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState({ name: "", category: "General", subject: "", body: "" });

  const load = () => api.get("/admin/templates").then(({ data }) => setTemplates(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) return toast.error("Template name required");
    if (editing.id) {
      await api.put(`/admin/templates/${editing.id}`, editing);
    } else {
      await api.post("/admin/templates", editing);
    }
    toast.success("Template saved");
    setEditing({ name: "", category: "General", subject: "", body: "" });
    load();
  };

  const remove = async (t) => {
    await api.delete(`/admin/templates/${t.id}`);
    load();
  };

  const byCategory = templates.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <Card title="Template editor" desc="Write once, reuse anywhere. Use {{name}}, {{company}}, {{country}} tokens.">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Name"><TextInput value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
          <Field label="Category">
            <select className="admin-input" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
              {["Intro", "Follow-up", "Quote", "General"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Subject"><TextInput value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} /></Field>
          <Field label="Body" className="sm:col-span-3"><TextArea rows={6} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} /></Field>
        </div>
        <div className="flex gap-3 mt-5">
          <button className="btn btn-sm btn-primary" onClick={save} data-testid="template-save-button">{editing.id ? "Update Template" : "Save Template"}</button>
          {editing.id && <button className="btn btn-sm btn-outline-light" onClick={() => setEditing({ name: "", category: "General", subject: "", body: "" })}>New</button>}
        </div>
      </Card>

      {Object.entries(byCategory).map(([cat, list]) => (
        <div key={cat} className="bg-[#0E1642] border border-white/10 rounded-2xl p-6">
          <h3 className="font-display font-bold text-white mb-3 text-sm uppercase tracking-wide text-cyan-400">{cat}</h3>
          {list.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
              <div><span className="text-white font-semibold text-sm">{t.name}</span><span className="text-slate-500 text-xs ml-3">{t.subject}</span></div>
              <div className="flex gap-3">
                <button onClick={() => setEditing(t)} className="text-xs font-semibold text-cyan-400 hover:text-cyan-300">Edit</button>
                <button onClick={() => remove(t)} className="p-1.5 text-slate-500 hover:text-red-400" aria-label="Delete template"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      ))}
      {templates.length === 0 && <div className="text-sm text-slate-500">No templates yet — create your first above.</div>}
    </div>
  );
}

function Compose() {
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [daily, setDaily] = useState(0);

  useEffect(() => {
    api.get("/admin/contacts").then(({ data }) => setContacts(data));
    api.get("/admin/templates").then(({ data }) => setTemplates(data));
    api.get("/admin/email-log").then(({ data }) => setDaily(data.today));
  }, []);

  const pickTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setTemplateName(t.name);
    setSubject(t.subject);
    setBody(t.body);
  };

  const send = async () => {
    if (!selected.length || !subject) return toast.error("Pick at least one lead and write a subject");
    try {
      await api.post("/admin/send-email", { contact_ids: selected, subject, body, template_name: templateName });
      toast.success("Queued for sending");
      const { data } = await api.get("/admin/email-log");
      setDaily(data.today);
      setSelected([]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <Card title="Compose" desc="Send to selected leads with a template. Emails send via SendGrid, or queue as pending until a key is set.">
        <div className="text-xs font-semibold text-cyan-300 mb-4" data-testid="email-daily-count">Sent today: {daily}</div>
        <Field label="Leads">
          <div className="rounded-xl border border-white/10 max-h-44 overflow-y-auto divide-y divide-white/5">
            {contacts.map((c) => (
              <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer hover:bg-white/[0.03] transition-colors">
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={(e) => setSelected(e.target.checked ? [...selected, c.id] : selected.filter((x) => x !== c.id))}
                  className="accent-cyan-400"
                  data-testid={`compose-contact-${c.id}`}
                />
                <span className="text-white font-semibold">{c.name}</span>
                <span className="text-slate-400">{c.email}</span>
              </label>
            ))}
            {contacts.length === 0 && <div className="px-4 py-6 text-center text-slate-500 text-sm">No contacts in the directory yet.</div>}
          </div>
        </Field>
        <Field label="Template">
          <select className="admin-input" value="" onChange={(e) => pickTemplate(e.target.value)}>
            <option value="">Start from scratch</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.category} · {t.name}</option>)}
          </select>
        </Field>
        <Field label="Subject"><TextInput value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        <Field label="Body"><TextArea rows={8} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
        <button className="btn btn-sm btn-primary mt-5" onClick={send} data-testid="email-send-button">Send</button>
      </Card>
    </div>
  );
}

function Log() {
  const [log, setLog] = useState([]);
  const [daily, setDaily] = useState(0);
  useEffect(() => {
    api.get("/admin/email-log").then(({ data }) => { setLog(data.log); setDaily(data.today); });
  }, []);

  const statusColor = { sent: "text-emerald-300", pending: "text-amber-300", failed: "text-red-400" };

  return (
    <Card title={`Sent log — ${daily} today`} desc="Every email attempt, per lead.">
      <table className="w-full text-sm" data-testid="admin-email-log-table">
        <thead>
          <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-white/10">
            <th className="pb-3 font-semibold">To</th><th className="pb-3 font-semibold">Subject</th><th className="pb-3 font-semibold">Template</th><th className="pb-3 font-semibold">Status</th><th className="pb-3 font-semibold">When</th>
          </tr>
        </thead>
        <tbody>
          {log.map((l) => (
            <tr key={l.id} className="border-b border-white/5" data-testid={`email-log-row-${l.id}`}>
              <td className="py-2.5 text-white font-semibold">{l.to}</td>
              <td className="py-2.5 text-slate-300">{l.subject}</td>
              <td className="py-2.5 text-slate-400 text-xs">{l.template_name || "—"}</td>
              <td className={`py-2.5 font-semibold capitalize ${statusColor[l.status] || ""}`}>{l.status}</td>
              <td className="py-2.5 text-slate-400 text-xs">{new Date(l.created_at).toLocaleString()}</td>
            </tr>
          ))}
          {log.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-500">No emails sent yet.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

export default function EmailTool({ limited = false }) {
  const [tab, setTab] = useState("compose");
  const tabs = limited ? TABS.filter((t) => t.key === "compose" || t.key === "contacts") : TABS;
  return (
    <div className="space-y-6" data-testid="admin-email-tool">
      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${tab === t.key ? "bg-[#00BFFF] text-[#06122B]" : "bg-[#0E1642] text-slate-300 border border-white/10 hover:border-cyan-400/40"}`}
            data-testid={`email-tab-${t.key}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "compose" && <Compose />}
      {tab === "contacts" && <Contacts />}
      {tab === "templates" && !limited && <Templates />}
      {tab === "log" && !limited && <Log />}
    </div>
  );
}
